import { Children, ReactNode, useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Box, IconButton, Stack } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

interface Props {
  children: ReactNode;
  slideBasis?: any;
  gap?: number;
  showArrows?: boolean;
  showDots?: boolean;
  align?: "start" | "center";
  fullHeight?: boolean;
  dragFree?: boolean;
  skipSnaps?: boolean;
  autoplayMs?: number;
  pauseOnHover?: boolean;
  /** Espaço vertical interno p/ sombra/hover sem cortar (overflow hidden do Embla) */
  contentGutter?: number;
}

const resolveFlexBasis = (slideBasis: any) => {
  if (typeof slideBasis === "string") {
    return slideBasis.includes(" ") ? slideBasis : `0 0 ${slideBasis}`;
  }

  if (slideBasis && typeof slideBasis === "object") {
    const resolved: Record<string, string> = {};
    Object.entries(slideBasis).forEach(([breakpoint, value]) => {
      resolved[breakpoint] =
        typeof value === "string" && !value.includes(" ")
          ? `0 0 ${value}`
          : String(value);
    });
    return resolved;
  }

  return "0 0 100%";
};

const Carousel = (props: Props) => {
  const {
    children,
    slideBasis = "100%",
    gap = 16,
    showArrows = true,
    showDots = true,
    align = "start",
    fullHeight = false,
    dragFree = false,
    skipSnaps = false,
    autoplayMs,
    pauseOnHover = true,
    contentGutter = 0,
  } = props;

  const slides = Children.toArray(children).filter(Boolean);
  const flexBasis = resolveFlexBasis(slideBasis);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align,
    containScroll: "trimSnaps",
    dragFree,
    skipSnaps,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [snapCount, setSnapCount] = useState(slides.length);
  const [isHovering, setIsHovering] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    setSnapCount(emblaApi.scrollSnapList().length);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    emblaApi?.reInit();
  }, [emblaApi, slides.length, dragFree, skipSnaps]);

  useEffect(() => {
    if (!emblaApi || !autoplayMs || slides.length <= 1) return;
    if (pauseOnHover && isHovering) return;

    const intervalId = window.setInterval(() => {
      if (emblaApi.canScrollNext()) {
        emblaApi.scrollNext();
      } else {
        emblaApi.scrollTo(0);
      }
    }, autoplayMs);

    return () => window.clearInterval(intervalId);
  }, [emblaApi, autoplayMs, pauseOnHover, isHovering, slides.length]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi],
  );

  const hasNavigation = slides.length > 1;

  return (
    <Stack
      sx={{ width: "100%", height: fullHeight ? "100%" : "auto" }}
      spacing={1}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <Box
        sx={{
          position: "relative",
          ...(fullHeight ? { flex: 1, minHeight: 0 } : {}),
        }}
      >
        <Box
          ref={emblaRef}
          sx={{
            overflow: "hidden",
            height: fullHeight ? "100%" : "auto",
            py: contentGutter,
          }}
        >
          <Box
            sx={{
              display: "flex",
              height: fullHeight ? "100%" : "auto",
              ml: `-${gap}px`,
            }}
          >
            {slides.map((child, index) => (
              <Box
                key={index}
                sx={{
                  flex: flexBasis,
                  minWidth: 0,
                  pl: `${gap}px`,
                  height: fullHeight ? "100%" : "auto",
                }}
              >
                {child}
              </Box>
            ))}
          </Box>
        </Box>
        {showArrows && hasNavigation && (
          <>
            <IconButton
              onClick={scrollPrev}
              disabled={!canScrollPrev}
              size="small"
              sx={{
                position: "absolute",
                top: "50%",
                left: 4,
                transform: "translate(-50%, -50%)",
                backgroundColor: "background.paper",
                boxShadow: 2,
                "&:hover": { backgroundColor: "background.paper" },
              }}
            >
              <ArrowBackIosNewIcon fontSize="inherit" />
            </IconButton>
            <IconButton
              onClick={scrollNext}
              disabled={!canScrollNext}
              size="small"
              sx={{
                position: "absolute",
                top: "50%",
                right: 4,
                transform: "translate(50%, -50%)",
                backgroundColor: "background.paper",
                boxShadow: 2,
                "&:hover": { backgroundColor: "background.paper" },
              }}
            >
              <ArrowForwardIosIcon fontSize="inherit" />
            </IconButton>
          </>
        )}
      </Box>
      {showDots && hasNavigation && (
        <Stack direction="row" justifyContent="center" spacing={0.5}>
          {Array.from({ length: snapCount }).map((_, index) => (
            <Box
              key={index}
              onClick={() => scrollTo(index)}
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                cursor: "pointer",
                backgroundColor:
                  index === selectedIndex ? "primary.main" : "action.disabled",
                transition: "background-color .2s",
              }}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default Carousel;
